import { useEffect, useMemo, useState } from "react";
import { AdminApp } from "./admin/AdminApp";
import { ArtworkPanel } from "./components/ArtworkPanel";
import { GalleryRoom } from "./components/GalleryRoom";
import { SiteChrome } from "./components/SiteChrome";
import { formatAvailability, formatArtworkPrice, loadArtworks } from "./lib/artworkApi";
import type { Artwork } from "./types/artwork";

export default function App() {
  const isAdmin = window.location.pathname.startsWith("/admin");
  const isCustomDomain = /(^|\.)janhusband\.com$/i.test(window.location.hostname);

  if (isAdmin && isCustomDomain) {
    return <AdminRedirect />;
  }

  if (isAdmin) {
    return <AdminApp />;
  }

  return <PublicSite />;
}

function AdminRedirect() {
  useEffect(() => {
    window.location.replace("https://jan-husband-gallery.pages.dev/admin");
  }, []);

  return <main className="boot">Opening protected admin.</main>;
}

function PublicSite() {
  const [artworks, setArtworks] = useState<Artwork[]>([]);
  const [selectedArtwork, setSelectedArtwork] = useState<Artwork | null>(null);
  const [menuOpen, setMenuOpen] = useState(false);

  useEffect(() => {
    let cancelled = false;
    loadArtworks().then((loaded) => {
      if (!cancelled) {
        setArtworks(loaded);
        setSelectedArtwork(null);
      }
    });
    return () => {
      cancelled = true;
    };
  }, []);

  const availableCount = useMemo(
    () => artworks.filter((artwork) => artwork.stockStatus === "instock").length,
    [artworks]
  );

  return (
    <>
      <SiteChrome onOpenMenu={() => setMenuOpen((open) => !open)} />
      {menuOpen && (
        <nav className="mobile-nav" aria-label="Mobile navigation">
          <a href="#gallery" onClick={() => setMenuOpen(false)}>
            Gallery
          </a>
          <a href="#about" onClick={() => setMenuOpen(false)}>
            About
          </a>
        </nav>
      )}
      <GalleryRoom artworks={artworks} selectedArtwork={selectedArtwork} onSelectArtwork={setSelectedArtwork} />
      <ArtworkPanel artwork={selectedArtwork} onClose={() => setSelectedArtwork(null)} />

      <main>
        <section className="content-band gallery-index" id="gallery">
          <div className="section-heading">
            <p className="eyebrow">{artworks.length} works</p>
            <h2>Gallery Catalogue</h2>
            <p>
              The catalogue is drawn from the old WordPress/WooCommerce site. {availableCount} works are currently
              marked available.
            </p>
          </div>
          <div className="artwork-grid">
            {artworks.map((artwork) => (
              <button
                className="artwork-card"
                type="button"
                key={artwork.id}
                onClick={() => {
                  setSelectedArtwork(artwork);
                }}
              >
                {artwork.imageUrl ? <img src={artwork.imageUrl} alt="" /> : <span>{artwork.title}</span>}
                <strong>{artwork.title}</strong>
                <small>
                  {formatArtworkPrice(artwork)} · {formatAvailability(artwork)}
                </small>
              </button>
            ))}
          </div>
        </section>

        <section className="content-band about-band" id="about">
          <div className="narrow-copy">
            <p className="eyebrow">About Jan</p>
            <h2>I am a self-taught abstract artist.</h2>
            <p>
              I work primarily with oil paints, and at times oils with cold wax. My work is predominantly of abstract
              landscapes or seascapes drawn from my own experience of places, with a particular emphasis on dramatic
              skies.
            </p>
          </div>
        </section>

        <section className="content-band contact-band" id="contact">
          <div className="narrow-copy">
            <p className="eyebrow">Contact</p>
            <h2>Enquire about paintings and commissions</h2>
            <p>
              Choose a painting in the gallery room or catalogue, or ask about a commissioned abstract work.
            </p>
          </div>
          <a className="primary-button" href="mailto:hello@janhusband.com">
            hello@janhusband.com
          </a>
        </section>
      </main>
    </>
  );
}
